/**
 * System Industry Templates
 * Pre-built templates for common industries
 * Google Ads API v22 Compatible
 */

import { IndustryTemplate } from '../types/industryTemplate';
import { JourneyMetdata } from '../types/marketingStrategy';

export function getSystemTemplates(): Omit<IndustryTemplate, 'id'>[] {
  const now = new Date();
  
  return [
    // =========================================================================
    // 1. AUTOMOTIVE / CAR DEALER
    // =========================================================================
    {
      industry: 'automotive',
      name: 'Automotive / Car Dealer',
      description: 'Für Autohäuser, Fahrzeughandel und stationären KFZ-Handel. Fokus auf Test Drive Bookings und lokale Reichweite.',
      markdownContent: `# 🚗 Automotive / Car Dealer Campaign Blueprint

## 🎯 Campaign Structure: ALWAYS Search (70%) + PMax (30%)

Diese Struktur ist MANDATORY für alle Automotive Campaigns.

### Search Campaign (70% Budget)
- **Bidding Strategy:** Target CPA €75
- **Campaign Type:** SEARCH (Google Ads API v22)
- **Network Settings:** 
  - target_google_search: true
  - target_search_network: true
  - target_content_network: false

**Keywords Strategy:**
INCLUDE (High Priority):
- "{Brand} {Model} probefahrt {Location}"
- "{Brand} händler {Location}"
- "{Model} gebrauchtwagen {Location}"
- "neuwagen {Brand} {Location}"
- "{Brand} {Model} preis"
- "test drive {Model}"

EXCLUDE (Negative Keywords - CRITICAL):
- "ersatzteile"
- "reparatur"
- "gebraucht teile"
- "schrott"
- "tuning"
- "werkstatt"

**Ad Copy Guidelines:**
Headlines (max 30 chars):
- "{Model} ab {Price}€"
- "Probefahrt buchen"
- "{DealerName} {Location}"
- "Jetzt verfügbar"
- "Beratung vor Ort"

Descriptions (max 90 chars):
- "Große Auswahl • Kompetente Beratung • Faire Preise • Finanzierung möglich"
- "Probefahrt vereinbaren und {Model} live erleben. Bei {DealerName} in {Location}"
- "Top Angebote für Neu- und Gebrauchtwagen. Inzahlungnahme möglich."

**Extensions (REQUIRED for Automotive):**
- Call Extension: Dealership phone number
- Location Extension: Store address with map
- Callout Extensions: "Kostenlose Probefahrt", "Finanzierung", "Inzahlungnahme"
- Sitelink Extensions: "Fahrzeugsuche", "Probefahrt buchen", "Finanzierung", "Kontakt"

### Performance Max Campaign (30% Budget)
- **Bidding Strategy:** Maximize Conversions
- **Campaign Type:** PERFORMANCE_MAX (Google Ads API v22)
- **Audience Signals:**
  - In-market: Vehicles & Automobiles
  - Affinity: Auto Enthusiasts
  - Custom: Previous website visitors

**Asset Requirements:**
- Headlines: 5-15 (use same as Search)
- Long Headlines: 1-5 (90 chars each)
- Descriptions: 2-5 (use same as Search)
- Images: Minimum 5 (landscape 1.91:1 + square 1:1)
- Videos: Optional but recommended (vehicle walkthroughs)
- Logo: Dealership logo
- Business Name: Dealership name (max 25 chars)

**Location Targeting:**
- Radius: 30km around dealership
- Bid Adjustment: +20% for high-income postal codes
- Exclude: Competitor dealership locations

## 🎯 Conversions (Priority Order)

1. **Test Drive Booking** (Primary)
   - Type: LEAD
   - Estimated Value: €150
   - Conversion Action: "Test Drive Buchung"
   - Category: QUALIFIED_LEAD

2. **Contact Form Submission** (Secondary)
   - Type: LEAD
   - Estimated Value: €100
   - Conversion Action: "Kontaktformular"
   - Category: LEAD

3. **Phone Call** (Tertiary)
   - Type: PHONE_CALL_FROM_ADS
   - Estimated Value: €80
   - Conversion Action: "Telefonanruf"
   - Minimum call duration: 60 seconds

## 📍 Location & Targeting

**Geographic Targeting:**
- Primary: 30km radius from dealership
- Secondary: Nearby cities (if large metro area)
- Exclude: Areas without service coverage

**Demographics (Optional):**
- Age: 25-65 (primary car buying age)
- Income: Top 50% (for new cars), All (for used cars)
- Parental Status: Parents (for family vehicles)

**Device Bid Adjustments:**
- Mobile: +10% (local searches happen on mobile)
- Desktop: 0% (baseline)
- Tablet: -10%

## ⏰ Ad Schedule

**Peak Hours (Bid +30%):**
- Monday-Friday: 9:00-12:00, 14:00-18:00
- Saturday: 9:00-14:00

**Low Hours (Bid -20%):**
- Monday-Friday: 18:00-21:00
- Sunday: All day

**Pause Completely:**
- When dealership is closed
- Late night: 22:00-8:00

## 💰 Budget Guidance

**Minimum Daily Budget:** €40
**Recommended Range:** €60-150 per day

**Allocation:**
- Search: 70% (€42-105)
- PMax: 30% (€18-45)

**Expected Performance:**
- Cost per Test Drive: €50-100
- Conversion Rate: 3-5%
- Click-Through Rate: 4-8%
- Quality Score Target: 7-9/10

## 💡 Best Practices (API v22 Compliant)

✅ **DO:**
- Use call extensions (critical for automotive)
- Use location extensions (show dealership on map)
- Target specific Brand + Model combinations
- Use exact match for brand terms
- Set up call tracking
- Use radius targeting around dealership
- Pause campaigns during closed hours
- Track test drive bookings as conversions

❌ **DON'T:**
- Bid on spare parts keywords (different intent)
- Use broad match for brand keywords (waste budget)
- Target nationwide (automotive is local business)
- Ignore negative keywords (causes waste)
- Forget call extensions (phones are critical)
- Mix new car and used car in same ad group
- Bid during closed hours

## 📊 Success Metrics

**Primary KPIs:**
- Target CPA: < €100 per test drive
- Conversion Rate: > 3%
- Quality Score: > 7/10
- Phone Calls: > 20% of conversions

**Secondary KPIs:**
- Click-Through Rate: > 4%
- Impression Share: > 50%
- Average Position: < 2.5

## 🚨 Common Pitfalls

1. **Too broad keywords** → Use "{Brand} {Model} {Location}" not just "auto kaufen"
2. **No negative keywords** → Waste budget on parts/repair searches
3. **Missing call extension** → Lose 40% of potential leads
4. **Wrong location targeting** → Targeting too far wastes budget
5. **No ad schedule** → Bidding when dealership closed wastes money

## 🔧 Technical Requirements (API v22)

- Campaign Type: SEARCH + PERFORMANCE_MAX
- Bidding: TARGET_CPA (Search), MAXIMIZE_CONVERSIONS (PMax)
- Conversion Tracking: REQUIRED (test drives, calls, forms)
- Extensions: Call + Location REQUIRED
- Radius Targeting: 30km default
`,
      campaignConfig: {
        searchBudgetPercent: 70,
        pmaxBudgetPercent: 30,
        searchBiddingStrategy: 'target_cpa',
        pmaxBiddingStrategy: 'maximize_conversions',
        targetCpa: 100,          // Updated: Realistic automotive CPA (was 75)
        targetRoas: 4.0          // NEW: Realistic ROAS target (400%)
      },
      campaignStructure: {
        brandCampaign: {
          enabled: true,
          budgetPercent: 20,
          adGroups: [
            {
              name: '{BusinessName} Brand',
              type: 'brand',
              keywordPatterns: [
                '{businessName} {location}',
                '{businessName} autohaus',
                '{businessName} bewertungen',
                '{businessName} kontakt',
                'autohaus {businessName}'
              ],
              priority: 1
            }
          ]
        },
        categoryCampaigns: {
          enabled: true,
          budgetPercentPerCategory: 25,
          categoryIdentifiers: {
            detectFrom: 'brands',
            minConfidence: 70
          },
          adGroups: [
            {
              name: '{Category} Brand',
              type: 'brand',
              keywordPatterns: [
                '{category} {businessName}',
                '{category} händler {location}',
                '{category} bei {businessName}',
                '{businessName} {category}'
              ],
              priority: 1
            },
            {
              name: '{Category} Generisch',
              type: 'generic',
              keywordPatterns: [
                '{category} kaufen {location}',
                '{category} angebote',
                '{category} {location}',
                '{category} händler'
              ],
              priority: 2
            },
            {
              name: '{Category} Neuwagen',
              type: 'product',
              keywordPatterns: [
                '{category} neuwagen {location}',
                'neuer {category}',
                '{category} neuwagen angebote',
                'neuwagen {category} {location}'
              ],
              priority: 3
            },
            {
              name: '{Category} Gebrauchtwagen',
              type: 'product',
              keywordPatterns: [
                '{category} gebrauchtwagen {location}',
                '{category} gebraucht',
                'gebrauchter {category}',
                '{category} gebraucht {location}'
              ],
              priority: 4
            },
            {
              name: '{Category} Aktionen',
              type: 'action',
              keywordPatterns: [
                '{category} aktion {location}',
                '{category} sonderangebot',
                '{category} angebot',
                '{category} rabatt'
              ],
              priority: 5,
              optional: true
            }
          ]
        }
      },
      requirements: {
        merchantCenter: false,
        callTracking: true,
        conversionTracking: true,
        minimumConversions: 10
      },
      primaryConversion: {
        type: 'booking',
        name: 'Test Drive Booking',
        estimatedValue: 180,      // Updated: Realistic value (was 150)
        conversionActionCategory: 'QUALIFIED_LEAD'
      },
      secondaryConversions: [
        {
          type: 'contact',
          name: 'Contact Form',
          estimatedValue: 100,
          conversionActionCategory: 'LEAD'
        },
        {
          type: 'lead',
          name: 'Phone Call',
          estimatedValue: 80,
          conversionActionCategory: 'LEAD'
        }
      ],
      minimumDailyBudget: 50,    // Updated: More realistic minimum (was 40)
      recommendedDailyBudget: { min: 80, max: 200 },  // Updated: Better range (was 60-150)
      isSystemTemplate: true,
      isActive: true,
      version: '1.0',
      currentVersion: '1.0',
      versionHistory: [],
      createdBy: 'system',
      createdAt: now,
      updatedAt: now,
      journeyMetadata: {
        defaultPhases: ['awareness', 'consideration', 'conversion'],
        preferredChannels: ['tiktok', 'meta', 'google_search'],
        fonts: {
          heading: 'Montserrat, sans-serif',
          body: 'Open Sans, sans-serif'
        }
      } as JourneyMetdata
    },

    // =========================================================================
    // 2. FINANCE (Insurance, Loans, Financial Services)
    // =========================================================================
    {
      industry: 'finance',
      name: 'Finance / Insurance / Loans',
      description: 'Für Versicherungen, Finanzierungen, Kredite und Beratungsdienstleistungen. Fokus auf qualifizierte Leads und Beratungsgespräche.',
      markdownContent: `# 💰 Finance / Insurance Campaign Blueprint

## 🎯 Campaign Structure: ALWAYS Search (65%) + PMax (35%)

### Search Campaign (65% Budget)
- **Bidding Strategy:** Target CPA €50
- **Campaign Type:** SEARCH (Google Ads API v22)

**Keywords Strategy:**
INCLUDE (High Intent):
- "{ServiceType} vergleich"
- "{ServiceType} online abschließen"
- "{ServiceType} beratung"
- "{ServiceType} rechner"
- "günstige {ServiceType}"
- "{ServiceType} {Location}"

EXCLUDE (Negative Keywords):
- "kostenlos" (low intent)
- "job" "karriere" (recruiting intent)
- "wiki" "definition" (informational only)

**Ad Copy (Compliance Required):**
Headlines:
- "Unverbindlich anfragen"
- "{ServiceType} Vergleich"
- "Persönliche Beratung"

Descriptions:
- "Kostenlose Erstberatung • Individuelle Lösungen • Schnelle Bearbeitung"
- "Jetzt Angebot anfordern und vergleichen. Persönliche Beratung inklusive."

**Extensions:**
- Call Extension: REQUIRED
- Callout: "Kostenlose Beratung", "Schnelle Bearbeitung", "Transparent"

### Performance Max Campaign (35% Budget)
- **Bidding Strategy:** Maximize Conversions
- **Audience Signals:**
  - Life Events: Home purchase, Job change, Marriage
  - In-market: Financial services
  - Demographics: Age 30-65, Income Top 50%

**Asset Requirements:**
- Trust signals: Professional images, office photos
- Compliance: No misleading claims
- Videos: Testimonials, explainer videos

## 🎯 Conversions

1. **Application Submitted** (Primary) - Value: €200
2. **Consultation Booked** (Secondary) - Value: €150
3. **Quote Request** (Tertiary) - Value: €80

## 📍 Targeting

- Location: Germany-wide or regional
- Demographics: Age 30-65, Income-based
- Device: Desktop +20% (complex forms)

## ⏰ Ad Schedule

- Business Hours: Mo-Fr 9-18 Uhr (+20% bid)
- Evenings: 18-21 Uhr (0% adjustment)
- Weekends: Reduced bids (-30%)

## 💡 Best Practices

✅ DO: Use trust signals, compliance wording, call tracking
❌ DON'T: Make unrealistic promises, ignore regulations

## 📊 Success Metrics

- Target CPA: < €80
- Conversion Rate: > 2%
- Quality Score: > 6/10
`,
      campaignConfig: {
        searchBudgetPercent: 65,
        pmaxBudgetPercent: 35,
        searchBiddingStrategy: 'target_cpa',
        pmaxBiddingStrategy: 'maximize_conversions',
        targetCpa: 80,           // Updated: Realistic finance CPA (was 50)
        targetRoas: 5.0          // NEW: Finance typically has higher ROAS
      },
      requirements: {
        merchantCenter: false,
        callTracking: true,
        conversionTracking: true,
        minimumConversions: 10
      },
      primaryConversion: {
        type: 'lead',
        name: 'Application Submitted',
        estimatedValue: 200,
        conversionActionCategory: 'QUALIFIED_LEAD'
      },
      secondaryConversions: [
        {
          type: 'booking',
          name: 'Consultation Booked',
          estimatedValue: 150,
          conversionActionCategory: 'LEAD'
        }
      ],
      minimumDailyBudget: 35,
      recommendedDailyBudget: { min: 50, max: 200 },
      isSystemTemplate: true,
      isActive: true,
      version: '1.0',
      currentVersion: '1.0',
      versionHistory: [],
      createdBy: 'system',
      createdAt: now,
      updatedAt: now,
      journeyMetadata: {
        defaultPhases: ['awareness', 'consideration', 'conversion'],
        preferredChannels: ['tiktok', 'meta', 'google_search'],
        fonts: {
          heading: 'Montserrat, sans-serif',
          body: 'Open Sans, sans-serif'
        }
      } as JourneyMetdata
    },

    // =========================================================================
    // 3. E-COMMERCE
    // =========================================================================
    {
      industry: 'ecommerce',
      name: 'E-Commerce / Online Shop',
      description: 'Für Online-Shops und E-Commerce. Fokus auf Product Sales mit Merchant Center Integration.',
      markdownContent: `# 🛒 E-Commerce Campaign Blueprint

## 🎯 Campaign Structure: ALWAYS Search (60%) + PMax (40%)

**CRITICAL:** Merchant Center integration REQUIRED!

### Search Campaign (60% Budget)
- **Bidding Strategy:** Target ROAS 4.0
- **Campaign Type:** SEARCH (Google Ads API v22)
- **Minimum Conversions:** 20 in last 30 days for ROAS bidding

**Keywords Strategy:**
INCLUDE (Product-focused):
- "{ProductCategory} kaufen"
- "{ProductCategory} online bestellen"
- "{Brand} {ProductCategory}"
- "günstig {ProductCategory}"
- "{ProductCategory} shop"
- "{ProductCategory} angebot"

EXCLUDE (Negative):
- "gebraucht"
- "kostenlos"
- "diy"
- "selber machen"
- "reparatur"

**Ad Copy:**
Headlines:
- "{ProductCategory} ab {Price}€"
- "Versandkostenfrei"
- "24h Lieferung"

Descriptions:
- "Große Auswahl • Schneller Versand • 30 Tage Rückgaberecht • Sichere Bezahlung"
- "{ProductCategory} online kaufen. Versandkostenfrei ab 50€. Jetzt bestellen!"

**Extensions:**
- Price Extensions: Show product prices
- Promotion Extensions: Sales, discounts
- Sitelink: "Sale", "Neuheiten", "Bestseller", "Kundenbewertungen"

### Performance Max Campaign (40% Budget)
- **Bidding Strategy:** Target ROAS 4.0
- **Merchant Center Feed:** REQUIRED
- **Campaign Type:** PERFORMANCE_MAX with Shopping feed

**Asset Requirements:**
- Product Feed: REQUIRED (via Merchant Center)
- Headlines: Product-focused, not brand-focused
- Images: Product photos from feed
- Videos: Product demos, unboxing (if available)
- Logo: Shop logo

**Audience Signals:**
- In-market: Shoppers for {ProductCategory}
- Affinity: Relevant interests
- Remarketing: Previous visitors

## 🎯 Conversions

1. **Purchase** (Primary) - Value: Dynamic (from transaction)
2. **Add to Cart** (Secondary) - Value: AOV * 0.3
3. **Begin Checkout** (Tertiary) - Value: AOV * 0.6

## 📍 Targeting

- Location: Germany-wide or EU
- Device: All devices (mobile-optimized mandatory)
- Demographics: Based on product category

## 💡 Best Practices

✅ DO:
- Optimize product feed quality
- Use dynamic remarketing
- Track all micro-conversions
- A/B test product images
- Use Shopping annotations

❌ DON'T:
- Ignore product feed errors
- Bid on brand-only keywords
- Use generic product descriptions
- Forget return policy in ads

## 📊 Success Metrics

- Target ROAS: > 4.0
- Conversion Rate: > 2%
- Cart Abandonment: < 70%
`,
      campaignConfig: {
        searchBudgetPercent: 60,
        pmaxBudgetPercent: 40,
        searchBiddingStrategy: 'target_roas',
        pmaxBiddingStrategy: 'target_roas',
        targetRoas: 5.5,         // Updated: Realistic e-commerce ROAS (was 4.0)
        targetCpa: 45            // NEW: Backup CPA strategy
      },
      requirements: {
        merchantCenter: true,  // REQUIRED for e-commerce
        callTracking: false,
        conversionTracking: true,
        minimumConversions: 20  // For ROAS bidding
      },
      primaryConversion: {
        type: 'purchase',
        name: 'Product Purchase',
        estimatedValue: 100,  // Will be dynamic from transaction
        conversionActionCategory: 'PURCHASE'
      },
      secondaryConversions: [
        {
          type: 'lead',
          name: 'Add to Cart',
          estimatedValue: 30,
          conversionActionCategory: 'PAGE_VIEW'
        }
      ],
      minimumDailyBudget: 50,
      recommendedDailyBudget: { min: 80, max: 300 },
      isSystemTemplate: true,
      isActive: true,
      version: '1.0',
      currentVersion: '1.0',
      versionHistory: [],
      createdBy: 'system',
      createdAt: now,
      updatedAt: now,
      journeyMetadata: {
        defaultPhases: ['awareness', 'consideration', 'conversion'],
        preferredChannels: ['tiktok', 'meta', 'google_search'],
        fonts: {
          heading: 'Montserrat, sans-serif',
          body: 'Open Sans, sans-serif'
        }
      } as JourneyMetdata
    },

    // =========================================================================
    // 4. PROFESSIONAL SERVICES
    // =========================================================================
    {
      industry: 'professional_services',
      name: 'Professional Services / B2B',
      description: 'Für B2B Dienstleistungen, Beratung, SaaS und professionelle Services. Fokus auf qualifizierte Leads und Demo-Anfragen.',
      markdownContent: `# 💼 Professional Services Campaign Blueprint

## 🎯 Campaign Structure: ALWAYS Search (60%) + PMax (40%)

### Search Campaign (60% Budget)
- **Bidding Strategy:** Maximize Conversions
- **Campaign Type:** SEARCH (Google Ads API v22)

**Keywords Strategy:**
INCLUDE (Problem + Solution):
- "{Service} dienstleistung"
- "{Service} anbieter"
- "{Service} software"
- "{Service} lösung"
- "{Service} beratung"
- "professionelle {Service}"

EXCLUDE (Negative):
- "kostenlos"
- "gratis"
- "selbst"
- "diy"
- "jobs"
- "ausbildung"

**Ad Copy:**
Headlines:
- "{Service} Experten"
- "Kostenlose Demo"
- "Jetzt testen"

Descriptions:
- "Professionelle {Service} Lösungen für Ihr Unternehmen. Demo anfragen!"
- "Kostenlose Testphase • Persönliche Beratung • Support inklusive"

**Extensions:**
- Callout: "Kostenlose Demo", "30 Tage Test", "24/7 Support"
- Sitelink: "Demo anfordern", "Preise", "Kundenstimmen", "Kontakt"

### Performance Max Campaign (40% Budget)
- **Bidding Strategy:** Maximize Conversions
- **Audience Signals:**
  - Job Title: Decision makers, C-level
  - Company Size: SMB to Enterprise
  - Industry: Relevant B2B industries

**Asset Requirements:**
- Headlines: Problem-solution focused
- Images: Professional team photos, product screenshots
- Videos: Product demo, customer testimonials

## 🎯 Conversions

1. **Demo Request** (Primary) - Value: €120
2. **Contact Form** (Secondary) - Value: €80
3. **Newsletter Signup** (Micro) - Value: €20

## 📍 Targeting

- Location: DACH region or global
- Demographics: Professional audience
- Device: Desktop +20% (B2B)

## 💡 Best Practices

✅ DO: Focus on benefits, use testimonials, offer free trials
❌ DON'T: Be too technical, ignore mobile, forget follow-up

## 📊 Success Metrics

- Target: 15-25 leads/month
- Cost per Lead: < €100
- Conversion Rate: > 2%
`,
      campaignConfig: {
        searchBudgetPercent: 60,
        pmaxBudgetPercent: 40,
        searchBiddingStrategy: 'target_cpa',    // Updated: More control
        pmaxBiddingStrategy: 'maximize_conversions',
        targetCpa: 120,          // NEW: Realistic B2B/SaaS CPA
        targetRoas: 4.5          // NEW: Professional services ROAS
      },
      requirements: {
        merchantCenter: false,
        callTracking: false,
        conversionTracking: true,
        minimumConversions: 5
      },
      primaryConversion: {
        type: 'signup',
        name: 'Demo Request',
        estimatedValue: 120,
        conversionActionCategory: 'SIGNUP'
      },
      secondaryConversions: [
        {
          type: 'contact',
          name: 'Contact Form',
          estimatedValue: 80,
          conversionActionCategory: 'LEAD'
        }
      ],
      minimumDailyBudget: 30,
      recommendedDailyBudget: { min: 50, max: 150 },
      isSystemTemplate: true,
      isActive: true,
      version: '1.0',
      currentVersion: '1.0',
      versionHistory: [],
      createdBy: 'system',
      createdAt: now,
      updatedAt: now
    }
  ];
}

